import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Settings } from 'lucide-react';
import { SkillDistribution } from '../Model';

interface SkillDistributionSliderProps {
  distribution: SkillDistribution;
  onDistributionChange: (distribution: SkillDistribution) => void;
  skillNames?: string[];
}

export function SkillDistributionSlider({ distribution, onDistributionChange, skillNames = [] }: SkillDistributionSliderProps) {
  const [localDistribution, setLocalDistribution] = useState<SkillDistribution>(distribution);
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalDistribution(distribution);
  }, [distribution]);

  const calculatePercentages = () => {
    const developing = Math.round(localDistribution.developing);
    const average = Math.round(localDistribution.average - localDistribution.developing);
    const good = Math.round(localDistribution.good - localDistribution.average);
    const excellent = Math.round(100 - localDistribution.good);

    return { developing, average, good, excellent };
  };

  const calculateScoreBoundaries = () => {
    const skillCount = skillNames.length || 5;
    const maxPossibleScore = skillCount * 5;
    const minPossibleScore = skillCount * 1;
    const scoreRange = maxPossibleScore - minPossibleScore;

    const excellentThreshold = Math.round(maxPossibleScore - (scoreRange * (100 - localDistribution.good) / 100));
    const goodThreshold = Math.round(maxPossibleScore - (scoreRange * (100 - localDistribution.average) / 100));
    const developingThreshold = Math.round(maxPossibleScore - (scoreRange * (100 - localDistribution.developing) / 100));

    return {
      developing: `≤ ${developingThreshold}`,
      average: `${developingThreshold + 1}-${goodThreshold}`,
      good: `${goodThreshold + 1}-${excellentThreshold}`,
      excellent: `≥ ${excellentThreshold + 1}`
    };
  };

  const getMarkerPosition = (value: number) => {
    return (value / 100) * 100;
  };

  const handleMouseDown = (marker: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(marker);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width) * 100;
    const clampedPosition = Math.max(5, Math.min(95, position));

    let newDistribution = { ...localDistribution };

    if (isDragging === 'developing') {
      newDistribution.developing = Math.round(Math.min(clampedPosition, newDistribution.average - 5));
    } else if (isDragging === 'average') {
      newDistribution.average = Math.round(Math.max(
        newDistribution.developing + 5,
        Math.min(clampedPosition, newDistribution.good - 5)
      ));
    } else if (isDragging === 'good') {
      newDistribution.good = Math.round(Math.max(newDistribution.average + 5, clampedPosition));
    }

    setLocalDistribution(newDistribution);
    onDistributionChange(newDistribution);
  }, [isDragging, localDistribution, onDistributionChange]);

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  const percentages = calculatePercentages();
  const scoreBoundaries = calculateScoreBoundaries();

  return (
    <div className="skill-distribution-panel">
      <div
        className="skill-distribution-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ marginBottom: isCollapsed ? 0 : '16px' }}
      >
        <div className="skill-distribution-title-row">
          <Settings size={14} className="skill-distribution-icon" />
          <h4 className="skill-distribution-title">
            Advanced Settings
          </h4>
        </div>

        <div className="skill-distribution-summary">
          {isCollapsed && (
            <span className="skill-distribution-summary-text">
              Skill thresholds configured
            </span>
          )}
          <span className={`expand-icon ${!isCollapsed ? 'expand-icon-expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <>
          <div className="mb-5">
            <div
              ref={sliderRef}
              className="skill-distribution-slider"
              style={{
                background: `linear-gradient(to right, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.15) ${localDistribution.developing}%, rgba(251, 191, 36, 0.15) ${localDistribution.developing}%, rgba(251, 191, 36, 0.15) ${localDistribution.average}%, rgba(59, 130, 246, 0.15) ${localDistribution.average}%, rgba(59, 130, 246, 0.15) ${localDistribution.good}%, rgba(34, 197, 94, 0.15) ${localDistribution.good}%, rgba(34, 197, 94, 0.15) 100%)`
              }}
            >
              <div className="slider-label" style={{ left: '8px', color: '#fca5a5' }}>
                Developing
              </div>

              <div
                className="slider-label"
                style={{
                  left: `${(localDistribution.developing + localDistribution.average) / 2}%`,
                  transform: 'translateX(-50%) translateY(-50%)',
                  color: '#fcd34d'
                }}
              >
                Average
              </div>

              <div
                className="slider-label"
                style={{
                  left: `${(localDistribution.average + localDistribution.good) / 2}%`,
                  transform: 'translateX(-50%) translateY(-50%)',
                  color: '#93c5fd'
                }}
              >
                Good
              </div>

              <div className="slider-label" style={{ right: '8px', color: '#86efac' }}>
                Excellent
              </div>

              {/* Developing marker */}
              <div
                className="slider-marker"
                style={{
                  left: `${getMarkerPosition(localDistribution.developing)}%`,
                  backgroundColor: 'rgba(239, 68, 68, 0.3)',
                  border: '3px solid #ef4444',
                  zIndex: isDragging === 'developing' ? 10 : 5
                }}
                onMouseDown={handleMouseDown('developing')}
                title={`Developing threshold: ${localDistribution.developing}%`}
              />

              {/* Average marker */}
              <div
                className="slider-marker"
                style={{
                  left: `${getMarkerPosition(localDistribution.average)}%`,
                  backgroundColor: 'rgba(251, 191, 36, 0.3)',
                  border: '3px solid #fbbf24',
                  zIndex: isDragging === 'average' ? 10 : 5
                }}
                onMouseDown={handleMouseDown('average')}
                title={`Average threshold: ${localDistribution.average}%`}
              />

              {/* Good marker */}
              <div
                className="slider-marker"
                style={{
                  left: `${getMarkerPosition(localDistribution.good)}%`,
                  backgroundColor: 'rgba(59, 130, 246, 0.3)',
                  border: '3px solid #3b82f6',
                  zIndex: isDragging === 'good' ? 10 : 5
                }}
                onMouseDown={handleMouseDown('good')}
                title={`Good threshold: ${localDistribution.good}%`}
              />
            </div>
          </div>

          <div className="skill-distribution-grid">
            <div
              className="skill-distribution-card"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)' }}
            >
              <div className="skill-distribution-card-label" style={{ color: '#fca5a5' }}>Developing</div>
              <div className="skill-distribution-card-value" style={{ color: '#fca5a5' }}>{scoreBoundaries.developing}</div>
              <div className="skill-distribution-card-percent" style={{ color: '#fca5a5' }}>({percentages.developing}%)</div>
            </div>

            <div
              className="skill-distribution-card"
              style={{ backgroundColor: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.25)' }}
            >
              <div className="skill-distribution-card-label" style={{ color: '#fcd34d' }}>Average</div>
              <div className="skill-distribution-card-value" style={{ color: '#fcd34d' }}>{scoreBoundaries.average}</div>
              <div className="skill-distribution-card-percent" style={{ color: '#fcd34d' }}>({percentages.average}%)</div>
            </div>

            <div
              className="skill-distribution-card"
              style={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.25)' }}
            >
              <div className="skill-distribution-card-label" style={{ color: '#93c5fd' }}>Good</div>
              <div className="skill-distribution-card-value" style={{ color: '#93c5fd' }}>{scoreBoundaries.good}</div>
              <div className="skill-distribution-card-percent" style={{ color: '#93c5fd' }}>({percentages.good}%)</div>
            </div>

            <div
              className="skill-distribution-card"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)' }}
            >
              <div className="skill-distribution-card-label" style={{ color: '#86efac' }}>Excellent</div>
              <div className="skill-distribution-card-value" style={{ color: '#86efac' }}>{scoreBoundaries.excellent}</div>
              <div className="skill-distribution-card-percent" style={{ color: '#86efac' }}>({percentages.excellent}%)</div>
            </div>
          </div>

          <div className="skill-distribution-hint">
            Drag the markers to adjust skill level boundaries
          </div>
        </>
      )}
    </div>
  );
}
